import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { FaUserCircle } from 'react-icons/fa';
import { playTaskSound } from '../utils/notificationSound';
import styled, { keyframes } from 'styled-components';

const slideInRight = keyframes`
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Item = styled.div`
  background: #e5f5ff;
  color: #000;
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: ${slideInRight} 0.3s ease-out;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }
`;

const AvatarWrap = styled.div`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  background: rgba(0,0,0,0.08);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  line-height: 1.4;
`;

const FromLabel = styled.div`
  font-size: 12px;
  opacity: 0.9;
  margin-bottom: 2px;
`;

const Name = styled.div`
  font-weight: 700;
  margin-bottom: 4px;
`;

const Text = styled.div`
  opacity: 0.95;
  margin-bottom: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PriorityBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #000;
  background: ${props => props.priority === 'high' ? 'rgba(231, 76, 60, 0.9)' :
    props.priority === 'medium' ? 'rgba(243, 156, 18, 0.9)' : 'rgba(39, 174, 96, 0.9)'};
`;

const CloseBtn = styled.button`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border: 2px solid #000;
  border-radius: 50%;
  background: #f18181;
  color: #000;
  cursor: pointer;
  font-size: 24px;
  font-weight: bold;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background 0.2s;

  &:hover {
    background: #f52f2f;
  }
`;

const PRIORITY_LABELS = {
  high: 'Высокий',
  medium: 'Средний',
  low: 'Низкий'
};

function briefText(str, maxLen = 80) {
  if (!str || typeof str !== 'string') return '';
  const t = str.trim();
  if (t.length <= maxLen) return t;
  return t.slice(0, maxLen) + '…';
}

const TASK_DISMISSED_KEY = 'taskNotificationDismissedIds';
const MAX_STORED_IDS = 500;

function loadDismissedIds() {
  try {
    const raw = localStorage.getItem(TASK_DISMISSED_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr.slice(-MAX_STORED_IDS) : []);
  } catch {
    return new Set();
  }
}

function saveDismissedIds(set) {
  try {
    const arr = [...set].slice(-MAX_STORED_IDS);
    localStorage.setItem(TASK_DISMISSED_KEY, JSON.stringify(arr));
  } catch (_) {}
}

export default function TaskNotifications() {
  const { state } = useApp();
  const [tasksAssignedToMe, setTasksAssignedToMe] = useState([]);
  const [users, setUsers] = useState({});
  const [dismissedIds, setDismissedIds] = useState(() => loadDismissedIds());
  const playedSoundForIds = useRef(new Set());

  const currentUserId = state.user?.id;

  const fetchData = useCallback(() => {
    if (!currentUserId) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    Promise.all([
      fetch('/api/tasks', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      fetch('/api/users', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json())
    ]).then(([tasksData, usersData]) => {
      const tasks = Array.isArray(tasksData) ? tasksData : [];
      const arr = Array.isArray(usersData) ? usersData : (Array.isArray(usersData?.users) ? usersData.users : []);
      const usersMap = Object.fromEntries((arr || []).map(u => [u.id, u]));
      setUsers(usersMap);
      const open = tasks.filter(t =>
        Number(t.assignedTo) === Number(currentUserId) &&
        (t.status === 'open' || t.status === '' || !t.status)
      );
      setTasksAssignedToMe(open);
    }).catch(() => {});
  }, [currentUserId]);

  useEffect(() => {
    if (!currentUserId) return;
    fetchData();
    const interval = setInterval(fetchData, 10000);
    const onRefresh = () => fetchData();
    window.addEventListener('show-tasks', onRefresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener('show-tasks', onRefresh);
    };
  }, [currentUserId, fetchData]);

  // Звук при появлении новой задачи (громкость 100%, через общий модуль звуков)
  const toShowIds = tasksAssignedToMe.filter(t => !dismissedIds.has(t.id)).map(t => t.id);
  useEffect(() => {
    if (toShowIds.length === 0) return;
    const newIds = toShowIds.filter(id => !playedSoundForIds.current.has(id));
    if (newIds.length === 0) return;
    newIds.forEach(id => playedSoundForIds.current.add(id));
    playTaskSound(1);
  }, [toShowIds.join(',')]);

  const handleDismiss = (taskId) => {
    setDismissedIds(prev => {
      const next = new Set([...prev, taskId]);
      saveDismissedIds(next);
      return next;
    });
  };

  if (!currentUserId || tasksAssignedToMe.length === 0) return null;

  const toShow = tasksAssignedToMe.filter(t => !dismissedIds.has(t.id));
  if (toShow.length === 0) return null;

  return (
    <Container>
      {toShow.map(task => {
        const author = users[task.authorId];
        const authorName = author?.username || (author?.first_name || author?.last_name
          ? `${author.first_name || ''} ${author.last_name || ''}`.trim()
          : null) || `Сотрудник #${task.authorId}`;
        const avatarUrl = author?.avatar;
        const priority = task.priority || 'medium';
        const brief = briefText(task.title || task.description || 'Без названия');

        return (
          <Item key={task.id}>
            <AvatarWrap>
              {avatarUrl ? (
                <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <FaUserCircle size={28} color="#333" />
              )}
            </AvatarWrap>
            <Body>
              <FromLabel>Вам назначена задача</FromLabel>
              <Name>{authorName}</Name>
              <Text title={task.title || task.description}>{brief}</Text>
              <PriorityBadge priority={priority}>
                {PRIORITY_LABELS[priority] || priority}
              </PriorityBadge>
            </Body>
            <CloseBtn
              type="button"
              onClick={(e) => { e.stopPropagation(); handleDismiss(task.id); }}
              aria-label="Закрыть"
              title="Закрыть уведомление"
            >
              ×
            </CloseBtn>
          </Item>
        );
      })}
    </Container>
  );
}
